// src/Pages/Shop/ShoppingCart.jsx
import { useState, useEffect, useCallback, useMemo } from "react";
import { Col, Container, Row } from 'react-bootstrap';
import { Parallax } from 'react-scroll-parallax';
import { m } from 'framer-motion';
import { fadeIn } from '../../Functions/GlobalAnimations';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';
import { ToastContainer, toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { CheckCircle, AlertCircle, Shield, ShoppingBag, Trash2, ArrowRight } from 'lucide-react';
import ImageWithAuth from "../../Components/ImageWithAuth";

// Composant pour chaque article du panier (design moderne)
const BasketItem = ({ item, onRemove, language, t }) => {
  const handleRemove = useCallback(() => {
    onRemove(item._id);
  }, [item._id, onRemove]);

  const itemTitle = useMemo(() => 
    item.title?.[language] || item.title?.fr || '',
    [item.title, language]
  );

  const itemDescription = useMemo(() => 
    item.description?.[language] || item.description?.fr || '',
    [item.description, language]
  );

  const levelsDisplay = useMemo(() => {
    if (!item.level?.length) return t('shopping_cart.no_level');
    return item.level
      .map(lvl => lvl?.name?.[language] || lvl?.name?.fr || lvl?._id || "Niveau inconnu")
      .join(" • ");
  }, [item.level, language, t]);

  const subLevelsDisplay = useMemo(() => {
    if (!item.sub_level?.length) return t('shopping_cart.no_sublevel');
    return item.sub_level
      .map(sub => sub?.name?.[language] || sub?.name?.fr || sub?._id || "Classe inconnue")
      .join(" • ");
  }, [item.sub_level, language, t]);

  return (
    <div
      className="bg-white rounded-3xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
      style={{
        animation: `fadeInUp 0.6s ease-out both`
      }}
    >
      <div className="p-6 min-md:p-8 flex flex-col min-md:flex-row gap-6 min-md:gap-8">
        {/* Image produit */}
        <div className="min-md:w-48 flex-shrink-0">
          <div className="relative overflow-hidden rounded-2xl h-56 min-md:h-64 bg-gray-100 shadow-inner">
            <ImageWithAuth
              src={item.picture ? `${config.SITE_API_URL}${item.picture}` : "https://images.unsplash.com/photo-1507842742364-a136af112d4d?w=400&h=500&fit=crop"}
              alt={itemTitle}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
          </div>
        </div>

        {/* Infos produit */}
        <div className="flex-1 flex flex-col justify-between">
          <div>
            <div className="flex flex-wrap gap-2 mb-3">
              {levelsDisplay && (
                <span className="text-xs font-medium px-3 py-1 rounded-full bg-blue-50 text-blue-700">
                  {levelsDisplay}
                </span>
              )}
              {item.matiere && (
                <span className="text-xs font-medium px-3 py-1 rounded-full bg-gray-100 text-gray-700">
                  {item.matiere.name?.[language] || item.matiere.name?.fr || item.matiere}
                </span>
              )}
              {!item.isActive && (
                <span className="text-xs font-medium px-3 py-1 rounded-full bg-red-50 text-red-700">
                  {t('shopping_cart.not_available')}
                </span>
              )}
            </div>

            <h3 className="text-xl min-md:text-xlg font-bold mb-2" style={{ color: '#003479' }}>
              {itemTitle}
            </h3>

            <p className="text-gray-600 mb-4 line-clamp-3">
              {itemDescription}
            </p>
          </div>

          <div className="flex flex-col min-sm:flex-row justify-between items-start min-sm:items-center gap-4 pt-4 border-t border-gray-100">
            <div>
              <p className="text-xxl font-bold" style={{ color: '#f29f2f' }}>
                {item.price_ttc?.toFixed(3)} {t('DT')}
              </p>
            </div>

            <div className="flex items-center gap-6">
              <button
                onClick={handleRemove}
                className="flex items-center gap-2 text-red-600 hover:text-red-800 font-medium transition-colors"
              >
                <Trash2 className="w-5 h-5" />
                <span>{t('shopping_cart.remove')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant pour l'en-tête parallaxe
const ParallaxHeader = ({ title }) => (
  <m.div className="h-[380px] overflow-hidden sm:h-[365px] relative" {...fadeIn}>
    <Parallax
      className="lg-no-parallax bg-cover absolute top-0 left-0 w-full h-full"
      translateY={[-40, 40]}
      style={{ backgroundImage: `url(/assets/img/webp/portfolio-bg.webp)` }}
    />
    <Container className="h-full relative">
      <Row className="items-stretch justify-center h-[450px] md:h-[375px] sm:h-[350px]">
        <Col xl={6} lg={7} md={8} xs={12} className="text-center flex justify-center flex-col">
          <h2 className="text-blue-900 mb-[0px] font-serif font-medium -tracking-[1px] my-auto xs:mb-[0px]">
            {title}
          </h2>
        </Col>
      </Row>
    </Container>
  </m.div>
);

// Panier vide
const EmptyCart = ({ onContinueShopping, t }) => (
  <div className="max-w-2xl mx-auto px-6 py-16 text-center">
    <div className="bg-white rounded-3xl shadow-xl p-12 border border-gray-100">
      <div className="text-xxxl mb-8">🛒</div>
      <h2 className="text-4xl font-bold mb-4" style={{ color: '#003479' }}>
        {t('shopping_cart.empty_cart')}
      </h2>
      <p className="text-gray-600 text-xl mb-10">
        {t('shopping_cart.items_in_cart')}
      </p>
      <button
        onClick={onContinueShopping}
        className="px-10 py-4 rounded-2xl font-bold text-lg text-white transition-all duration-300 hover:shadow-2xl hover:scale-105"
        style={{ backgroundColor: '#f29f2f' }}
      >
        {t('shopping_cart.continue_shopping')}
      </button>
    </div>
  </div>
);

// Popup de Confirmation
const ClearConfirmPopup = ({ show, onClose, onConfirm, itemCount, total, t }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/60 transition-opacity" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md mx-auto overflow-hidden border-2 border-red-100">
          <div className="p-6 min-md:p-8 text-center border-b border-red-100">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-red-50 mb-6">
              <Trash2 className="h-10 w-10 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-red-800 mb-2">
              {t('shopping_cart.clear_confirmation_title')}
            </h3>
          </div>

          <div className="p-6 min-md:p-6 bg-red-50/30">
            <div className="flex justify-between items-center mb-4">
              <span className="font-medium text-gray-700">{t('shopping_cart.items_count')}:</span>
              <span className="font-bold text-lg" style={{ color: '#003479' }}>{itemCount}</span>
            </div>
            <div className="flex justify-between items-center mb-6">
              <span className="font-medium text-gray-700">{t('shopping_cart.total_value')}:</span>
              <span className="font-bold text-xl" style={{ color: '#f29f2f' }}>{total.toFixed(3)} {t('DT')}</span>
            </div>
            <p className="text-sm text-red-600 text-center italic">
              ⚠️ {t('shopping_cart.clear_warning')}
            </p>
          </div>

          <div className="p-6 min-md:p-6 bg-gray-50 flex flex-col min-sm:flex-row gap-4">
            <button
              onClick={onClose}
              className="flex-1 py-4 px-6 rounded-xl font-bold text-md text-gray-700 bg-white border-2 border-gray-300 hover:bg-gray-50 transition-all duration-300"
            >
              {t('shopping_cart.cancel')}
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 py-4 px-6 rounded-xl font-bold text-md text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              {t('shopping_cart.confirm_clear')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ShoppingCart = (props) => {
  const { t, i18n } = useTranslation();
  const [basket, setBasket] = useState([]);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const navigate = useNavigate();

  const direction = i18n.language === 'ar' ? 'rtl' : 'ltr';
  const currentLanguage = i18n.language;

  // Calcul des informations d'abonnement
  const subscriptionInfo = useMemo(() => {
    if (!activeSubscription) return null;
    
    const remainingDocs = activeSubscription.number_document || 0;
    const basketCount = basket.length;
    const willCover = remainingDocs >= basketCount;
    const remainingAfter = willCover ? remainingDocs - basketCount : remainingDocs;
    
    return {
      remainingDocs,
      basketCount,
      willCover,
      remainingAfter,
      subscriptionCode: activeSubscription.code_subscription,
      endDate: activeSubscription.date_fin_subscription,
      isActive: activeSubscription.isActive && activeSubscription.etat_subscription === 'en cours'
    };
  }, [activeSubscription, basket]);

  // Calculs memoizés
  const subtotal = useMemo(() => 
    basket.reduce((sum, item) => sum + (item.price_ttc || 0), 0),
    [basket]
  );

  const total = useMemo(() => subtotal, [subtotal]);
  const finalTotal = subscriptionInfo?.willCover ? 0 : total;

  const itemCount = useMemo(() => basket.length, [basket]);

  const hasUnavailableItems = useMemo(() => 
    basket.some(item => !item.isActive),
    [basket]
  );

  // Fetch panier et abonnement
  const fetchData = useCallback(async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      toast.error(t('shopping_cart.errors.login_required'));
      navigate("/login");
      return;
    }

    try {
      const [basketResponse, subResponse] = await Promise.all([
        axios.get(`${config.SITE_API_URL}/get-basket`, {
          headers: { Authorization: `Bearer ${token}`, 'x-api-key': config.SITE_API_KEY },
        }),
        axios.get(`${config.SITE_API_URL}/getactivesubscriptionbyuser`, {
          headers: { Authorization: `Bearer ${token}`, 'x-api-key': config.SITE_API_KEY },
        })
      ]);

      setBasket(basketResponse.data.basket || []);
      const activeSub = subResponse.data?.[0] || null;
      setActiveSubscription(activeSub);
    } catch (error) {
      toast.error(error.response?.data?.message || t('shopping_cart.errors.fetch_error'));
    } finally {
      setLoading(false);
    }
  }, [navigate, t]);

  const handleRemoveFromBasket = useCallback(async (documentId) => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    try {
      const res = await axios.post(
        `${config.SITE_API_URL}/remove-from-basket`,
        { documentId },
        { headers: { Authorization: `Bearer ${token}`, 'x-api-key': config.SITE_API_KEY } }
      );
      setBasket(prev => prev.filter(item => item._id !== documentId));
      toast.success(res.data.message || t('shopping_cart.remove_success'));
    } catch (error) {
      toast.error(error.response?.data?.message || t('shopping_cart.errors.remove_error'));
    }
  }, [t]);

  const handleClearBasket = useCallback(() => {
    setShowClearConfirm(true);
  }, []);

  const handleConfirmClearBasket = useCallback(async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    try {
      const res = await axios.post(
        `${config.SITE_API_URL}/clear-basket`,
        {},
        { headers: { Authorization: `Bearer ${token}`, 'x-api-key': config.SITE_API_KEY } }
      );
      setBasket([]);
      setShowClearConfirm(false);
      toast.success(res.data.message || t('shopping_cart.clear_all_success'));
    } catch (error) {
      toast.error(error.response?.data?.message || t('shopping_cart.errors.clear_error'));
      setShowClearConfirm(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" style={{ direction }}>
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#f29f2f]"></div>
      </div>
    );
  }

  if (!basket.length) {
    return (
      <div className="min-h-screen bg-gray-50" style={{ direction }}>
        <ParallaxHeader title={t('shopping_cart.title')} />
        <EmptyCart onContinueShopping={() => navigate('/course')} t={t} />
      </div>
    );
  }

  return (
    <div style={{ direction }}>
      {/* Fond décoratif subtil */}
      <div className="fixed inset-0 pointer-events-none opacity-10">
        <div className="absolute top-20 right-20 w-80 h-80 rounded-full blur-3xl" style={{ backgroundColor: '#f29f2f' }} />
        <div className="absolute bottom-32 left-10 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: '#0879bd' }} />
      </div>

      <ParallaxHeader title={t('shopping_cart.title')} />

      <main className="max-w-7xl mx-auto px-4 min-sm:px-6 min-lg:px-8 py-12 min-md:py-16">
        <div className="grid min-lg:grid-cols-3 gap-8 xl:gap-10">
          {/* Liste des articles */}
          <div className="min-lg:col-span-2 space-y-6 order-1 min-lg:order-2">
            {basket.map((item) => (
              <BasketItem
                key={item._id}
                item={item}
                onRemove={handleRemoveFromBasket}
                language={currentLanguage}
                t={t}
              />
            ))}

            <div className="flex justify-center min-sm:justify-between mt-8 gap-3">
              <Link to="/course">
                <button className="flex items-center gap-3 px-8 py-4 rounded-2xl font-medium text-lg text-[#003479] border-2 border-[#003479] hover:bg-[#003479] hover:text-white transition-all duration-300">
                  <ShoppingBag className="w-6 h-6" />
                  {t('shopping_cart.continue_shopping')}
                </button>
              </Link>
              
              <button
                onClick={handleClearBasket}
                disabled={!basket.length}
                className={`px-8 py-3 rounded-2xl font-bold text-xl transition-all duration-300 hover:shadow-xl hover:scale-[1.02] active:scale-98 ${
                  basket.length 
                    ? 'text-red-600 border-2 border-red-600 hover:bg-red-600 hover:text-white' 
                    : 'text-gray-400 border-2 border-gray-300 cursor-not-allowed'
                }`}
              >
                {t('shopping_cart.clear_all')}
              </button>
            </div>
          </div>

          {/* Résumé commande (sticky) */}
          <div className="min-lg:col-span-1 order-2 min-lg:order-1">
            <div
              className="rounded-3xl p-8 min-md:p-10 shadow-2xl sticky top-8"
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
                borderLeft: '8px solid #f29f2f',
              }}
            >
              <h2 className="text-xlg font-bold mb-10" style={{ color: '#003479' }}>
                {t('shopping_cart.order_summary')}
              </h2>

              {/* Carte d'abonnement actif */}
              {subscriptionInfo && subscriptionInfo.isActive && (
                <div className={`mb-6 p-3 rounded-xl ${subscriptionInfo.willCover ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                  <div className="flex items-start gap-3">
                    {subscriptionInfo.willCover ? (
                      <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="h-6 w-6 text-yellow-600 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <p className="font-bold text-sm mb-2" style={{ color: subscriptionInfo.willCover ? '#166534' : '#854d0e' }}>
                        🎓 {t('shopping_cart.active_subscription')}
                      </p>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('shopping_cart.remaining_documents')}:</span>
                          <strong className={subscriptionInfo.willCover ? 'text-green-700' : 'text-yellow-700'}>
                            {subscriptionInfo.remainingDocs}
                          </strong>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('shopping_cart.documents_in_cart')}:</span>
                          <strong className={subscriptionInfo.willCover ? 'text-green-700' : 'text-red-600'}>
                            {subscriptionInfo.basketCount}
                          </strong>
                        </div>

                        {subscriptionInfo.willCover ? (
                          <>
                            <div className="flex justify-between pt-2 border-t border-green-200">
                              <span className="text-gray-600">{t('shopping_cart.remaining_after_purchase')}:</span>
                              <strong className="text-green-700 text-lg">
                                {subscriptionInfo.remainingAfter}
                              </strong>
                            </div>
                            <div className="mt-3 p-2 bg-green-100 rounded-lg text-center">
                              <span className="text-green-800 font-medium text-xs">
                                ✓ {t('shopping_cart.free_checkout')}
                              </span>
                            </div>
                          </>
                        ) : (
                          <div className="mt-2 p-2 bg-red-50 rounded-lg">
                            <p className="text-red-700 text-xs">
                              ⚠️ {t('shopping_cart.subscription_insufficient', { 
                                remaining: subscriptionInfo.remainingDocs, 
                                needed: subscriptionInfo.basketCount 
                              })}
                            </p>
                          </div>
                        )}
                      </div>

                      {subscriptionInfo.endDate && (
                        <p className="text-xs text-gray-500 mt-3 pt-2 border-t border-gray-200">
                          📅 {t('shopping_cart.valid_until')}: {new Date(subscriptionInfo.endDate).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
 {!subscriptionInfo && (
              <div className="space-y-6 mb-10">
                <div className="flex flex-col items-center pb-5 border-b-2 border-gray-200">
                  <span className="text-gray-600 font-medium text-lg">
                    {t('shopping_cart.subtotal', { count: itemCount })}
                  </span>
                  <span className="font-bold text-xlg mt-3" style={{ color: '#003479' }}>
                    {subtotal.toFixed(3)} {t('DT')}
                  </span>
                </div>

               <div className="flex flex-row justify-between items-center pt-5 text-xxl font-bold" style={{ color: '#003479' }}>
                  <span>{t('shopping_cart.total_to_pay')}</span>
                  <span>{finalTotal.toFixed(3)} {t('DT')}</span>
                </div>
              </div>)}

              <Link to="/checkout">
                <button
                  className="w-full py-3 rounded-2xl font-bold text-xl text-white transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] active:scale-98 flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: subscriptionInfo?.willCover ? '#10b981' : '#0879bd',
                    boxShadow: subscriptionInfo?.willCover 
                      ? '0 12px 35px rgba(16, 185, 129, 0.35)' 
                      : '0 12px 35px rgba(8, 121, 189, 0.35)',
                  }}
                >
                  {subscriptionInfo?.willCover ? t('shopping_cart.checkout_free') : t('shopping_cart.checkout')}
                </button>
              </Link>

              <div className="mt-8 p-3 rounded-xl text-center text-sm font-medium bg-green-50 text-green-800 border border-green-200">
                <Shield className="inline-block w-4 h-4 mr-2" />
                {t('shopping_cart.secure_payment')} - {t('shopping_cart.data_protected')}
              </div>

              {hasUnavailableItems && (
                <p className="mt-6 text-sm text-red-600 text-center font-medium">
                  {t('shopping_cart.items_not_available')}
                </p>
              )}
            </div>
          </div>
        </div>
      </main>

      <ToastContainer position={currentLanguage === 'ar' ? 'bottom-right' : 'bottom-left'} autoClose={3000} />

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(25px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <ClearConfirmPopup
        show={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        onConfirm={handleConfirmClearBasket}
        itemCount={itemCount}
        total={total}
        t={t}
      />
    </div>
  );
};

export default ShoppingCart;